import { useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";

import * as alertsApi from "../../helpers/api/admin-alerts";
import type { AlertInbox, AlertItem } from "../../helpers/api/admin-alerts";
import { APICore } from "../../helpers/api/apiCore";
import { unwrapItem } from "../inventory/unwrap";
import bellSound from "../../assets/sound/mixkit-bell-notification-933.wav";

// =============================================================================
// Admin alerts in the portal: the bell's inbox, polled, plus the sound and the
// "something new arrived" signal. The query is shared (react-query), and the
// newest-id watermark lives at module level, so moving between pages neither
// refetches from scratch nor replays the sound.
// =============================================================================

export const ADMIN_ALERTS_KEY = "admin-alerts-inbox";
const POLL_MS = 20_000;
const SOUND_KEY = "adminAlerts.sound";

/** Sound is a per-device choice (on by default): a shared office machine may want it off. */
export const isSoundOn = (): boolean => {
  try {
    return window.localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
};

export const setSoundOn = (on: boolean) => {
  try {
    window.localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  } catch {
    /* storage blocked: the default (on) stays */
  }
};

// Browsers block audio until the user has interacted with the page once; prime
// an element on the first click or key press so later alerts can play.
let audio: HTMLAudioElement | null = null;
let unlockArmed = false;
const armAudioUnlock = () => {
  if (unlockArmed || typeof window === "undefined") return;
  unlockArmed = true;
  const unlock = () => {
    try {
      audio = audio ?? new Audio(bellSound);
      audio.muted = true;
      void audio.play()?.then(() => {
        audio!.pause();
        audio!.currentTime = 0;
        audio!.muted = false;
      }).catch(() => undefined);
    } catch {
      /* no audio support: alerts still show */
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
};

export const playAlertSound = () => {
  if (!isSoundOn()) return;
  try {
    audio = audio ?? new Audio(bellSound);
    audio.muted = false;
    audio.currentTime = 0;
    void audio.play()?.catch(() => undefined);
  } catch {
    /* blocked by autoplay policy: the toast and badge still show */
  }
};

// Newest notification id already seen in this tab. null = not loaded yet: the first
// load sets the baseline silently, so opening the portal never plays a backlog.
let watermark: number | null = null;
type Listener = (items: AlertItem[]) => void;
const listeners = new Set<Listener>();

/** Subscribe to unread notifications that arrived since the last poll. */
export const onNewAlerts = (fn: Listener) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Pure: the unread items newer than `mark` (exported for tests). */
export const newSince = (items: AlertItem[], mark: number | null): AlertItem[] =>
  mark == null ? [] : items.filter((i) => i.id > mark && !i.readAt);

const observe = (inbox: AlertInbox | undefined) => {
  if (!inbox) return;
  const top = inbox.items.reduce((m, i) => Math.max(m, i.id), 0);
  const fresh = newSince(inbox.items, watermark);
  watermark = Math.max(watermark ?? 0, top);
  if (fresh.length) {
    playAlertSound();
    listeners.forEach((l) => l(fresh));
  }
};

export const useAdminAlerts = () => {
  const api = new APICore();
  const qc = useQueryClient();
  useEffect(armAudioUnlock, []);

  const { data, isLoading } = useQuery(ADMIN_ALERTS_KEY, () => alertsApi.inbox({ limit: 20 }), {
    enabled: api.isUserAuthenticated(),
    refetchInterval: POLL_MS,
    // An alert is for when the admin is looking elsewhere, so keep polling in the background.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false,
    onSuccess: (res) => observe(unwrapItem<AlertInbox>(res)),
  });
  const inbox = unwrapItem<AlertInbox>(data) ?? { items: [], unread: 0, hasMore: false };

  const refresh = () => qc.invalidateQueries(ADMIN_ALERTS_KEY);
  const markRead = async (id: number) => {
    await alertsApi.markRead(id).catch(() => undefined);
    refresh();
  };
  const markAllRead = async () => {
    await alertsApi.markAllRead().catch(() => undefined);
    refresh();
  };

  return { ...inbox, isLoading, markRead, markAllRead, refresh };
};
